import { useQuery } from '@tanstack/react-query'
import { addDays, addMinutes, startOfDay } from 'date-fns'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/integrations/supabase/client'

export function useBookingFlow(_slug: string, company: any) {
  const servicesQuery = useQuery({
    queryKey: ['public-services', company?.id],
    enabled: !!company?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_public_services', { p_company_id: company!.id })
      if (error) {
        console.error('[agendar] services error', error)
        return []
      }
      return (data ?? []) as any[]
    },
    retry: false,
  })

  const collaboratorsQuery = useQuery({
    queryKey: ['public-collaborators', company?.id],
    enabled: !!company?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_public_collaborators', { p_company_id: company!.id })
      if (error) {
        console.error('[agendar] collaborators error', error)
        return []
      }
      return (data ?? []) as any[]
    },
    retry: false,
  })

  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1)
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([])
  const [washerId, setWasherId] = useState<string>('')
  const [date, setDate] = useState<Date>(startOfDay(new Date()))
  const [time, setTime] = useState<string>('')
  
  const [nome, setNome] = useState('')
  const [phone, setPhone] = useState('')
  const [plate, setPlate] = useState('')
  const [vehicleModel, setVehicleModel] = useState('')
  const [vehicleColor, setVehicleColor] = useState('')
  const [email, setEmail] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [confirmed, setConfirmed] = useState<string | null>(null)

  const selectedServices = useMemo(() => {
    return (servicesQuery.data ?? []).filter((s: any) => selectedServiceIds.includes(s.id))
  }, [selectedServiceIds, servicesQuery.data])

  const totalDuration = useMemo(() => {
    return selectedServices.reduce((acc: any, curr: any) => acc + (curr.estimated_time_minutes ?? 30), 0)
  }, [selectedServices])

  const totalPrice = useMemo(() => {
    return selectedServices.reduce((acc: any, curr: any) => acc + (curr.price ?? 0), 0)
  }, [selectedServices])

  const washer = useMemo(() => {
    return (collaboratorsQuery.data ?? []).find((w: any) => w.id === washerId)
  }, [washerId, collaboratorsQuery.data])

  const dayStart = useMemo(() => startOfDay(date).toISOString(), [date])
  const dayEnd = useMemo(() => addDays(startOfDay(date), 1).toISOString(), [date])

  const busyQuery = useQuery({
    queryKey: ['busy', company?.id, dayStart],
    enabled: !!company?.id,
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .rpc('get_public_busy_slots', { 
            p_company_id: company!.id, 
            p_start_date: dayStart, 
            p_end_date: dayEnd 
          })
        if (error) {
          console.error('[agendar] busy error', error)
          return []
        }
        return data ?? []
      } catch (e) {
        console.error('[agendar] busy exception', e)
        return []
      }
    },
    retry: false,
  })

  const slots = useMemo(() => {
    const list = []
    const startTotal = 8 * 60
    const endTotal = 18 * 60

    for (let min = startTotal; min < endTotal; min += 30) {
      const h = Math.floor(min / 60)
      const m = min % 60
      list.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
    }
    return list
  }, [])

  const days = useMemo(
    () => Array.from({ length: 14 }, (_, i) => addDays(startOfDay(new Date()), i)),
    []
  )

  const unavailable = useMemo(() => {
    const set = new Set<string>()
    const list = busyQuery.data ?? []
    const now = Date.now()

    for (const t of slots) {
      const [hh, mm] = t.split(':').map(Number)
      const slotStart = addMinutes(addMinutes(startOfDay(date), hh * 60), mm).getTime()
      const slotEnd = slotStart + totalDuration * 60_000

      if (slotStart < now) {
        set.add(t)
        continue
      }

      // Check if all active washers are busy
      let availableWashers = collaboratorsQuery.data ?? []
      if (washerId) {
        availableWashers = availableWashers.filter((w: any) => w.id === washerId)
      }

      let busyCount = 0
      for (const w of availableWashers) {
        let isWasherBusy = false
        for (const b of list) {
          if (b.assigned_to !== w.id && b.assigned_to) continue
          if (!b.scheduled_at) continue

          const bStart = new Date(b.scheduled_at).getTime()
          const bServices = b.services as any[]
          const bDuration = (bServices || []).reduce((acc, curr) => acc + (curr.estimated_time_minutes ?? 30), 0) || 30
          const bEnd = bStart + bDuration * 60_000

          if (slotStart < bEnd && bStart < slotEnd) {
            isWasherBusy = true
            break
          }
        }
        if (isWasherBusy) busyCount++
      }

      if (availableWashers.length > 0 && busyCount >= availableWashers.length) {
        set.add(t)
      }
    }
    return set
  }, [busyQuery.data, slots, date, totalDuration, collaboratorsQuery.data, washerId])

  const submit = async () => {
    if (!company || selectedServices.length === 0 || !time) return
    setSubmitting(true)
    try {
      const [hh, mm] = time.split(':').map(Number)
      const scheduledAt = addMinutes(addMinutes(date, hh * 60), mm)
      const cleanPlate = plate.toUpperCase().replace(/[^A-Z0-9]/g, '')

      const { data: orderId, error: orderErr } = await supabase
        .rpc('create_public_booking', {
          p_company_id: company.id,
          p_customer_name: nome,
          p_customer_phone: phone,
          p_vehicle_plate: cleanPlate,
          p_vehicle_model: vehicleModel,
          p_vehicle_color: vehicleColor,
          p_services: selectedServices,
          p_total_price: totalPrice,
          p_assigned_to: washerId || null,
          p_scheduled_at: scheduledAt.toISOString()
        })

      if (orderErr) {
        setSubmitting(false)
        toast.error('Erro ao agendar: ' + orderErr.message)
        return
      }

      setSubmitting(false)
      setConfirmed(orderId)
      toast.success('Agendamento confirmado!')
    } catch (e: any) {
      console.error('[agendar] submit exception', e)
      setSubmitting(false)
      toast.error('Erro inesperado ao agendar. Tente novamente.')
    }
  }

  return {
    step, setStep,
    selectedServiceIds, setSelectedServiceIds,
    washerId, setWasherId,
    date, setDate,
    time, setTime,
    nome, setNome,
    phone, setPhone,
    plate, setPlate,
    vehicleModel, setVehicleModel,
    vehicleColor, setVehicleColor,
    email, setEmail,
    submitting,
    confirmed,
    selectedServices,
    washer,
    slots,
    days,
    unavailable,
    submit,
    servicesQuery,
    collaboratorsQuery,
    busyQuery,
    totalDuration,
    totalPrice
  }
}
